import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format } from 'date-fns';
import { id } from 'date-fns/locale';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Kompres & perkecil gambar (base64/URL) menjadi JPEG kecil agar ukuran
 * PDF tidak membengkak. Tanda tangan / logo beresolusi tinggi dikecilkan
 * ke dimensi tampilan yang wajar lalu di-encode ulang sebagai JPEG.
 * Mengembalikan data URL JPEG, atau nilai asli bila gagal.
 * @param {string} src - Sumber gambar (data URL atau URL)
 * @param {number} maxWidth - Lebar maksimum dalam piksel
 * @param {number} maxHeight - Tinggi maksimum dalam piksel
 * @param {number} quality - Kualitas JPEG (0-1)
 * @returns {Promise<string|null>}
 */
const compressImage = (src, maxWidth = 500, maxHeight = 300, quality = 0.7) => {
  return new Promise((resolve) => {
    if (!src) {
      resolve(null);
      return;
    }
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          let width = img.naturalWidth || img.width;
          let height = img.naturalHeight || img.height;
          if (!width || !height) {
            resolve(src);
            return;
          }
          const ratio = Math.min(maxWidth / width, maxHeight / height, 1);
          width = Math.max(1, Math.round(width * ratio));
          height = Math.max(1, Math.round(height * ratio));

          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          // Latar putih agar tanda tangan PNG transparan tetap terlihat
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, width, height);
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', quality));
        } catch (e) {
          console.error('Error compressing image:', e);
          resolve(src);
        }
      };
      img.onerror = () => resolve(src);
      img.src = src;
    } catch (e) {
      resolve(src);
    }
  });
};

/**
 * Kompres seluruh signatureURL pada array evaluator (pembimbing/penguji).
 * @param {Array} evaluators
 * @returns {Promise<Array>}
 */
const compressEvaluatorSignatures = async (evaluators = []) => {
  return Promise.all((evaluators || []).map(async (ev) => {
    if (ev && ev.signatureURL) {
      const compressed = await compressImage(ev.signatureURL, 400, 200, 0.7);
      return { ...ev, signatureURL: compressed || ev.signatureURL };
    }
    return ev;
  }));
};

/**
 * Load logo image and convert to base64
 * Can load from public folder or URL
 */
const loadLogoAsBase64 = async (logoPath) => {
  try {
    let imageUrl = logoPath;

    // If path doesn't start with http, assume it's from public folder
    if (!logoPath.startsWith('http')) {
      // Remove leading slash if exists
      const cleanPath = logoPath.startsWith('/') ? logoPath.substring(1) : logoPath;
      imageUrl = `/${cleanPath}`;
    }

    // Fetch the image
    const response = await fetch(imageUrl);
    if (!response.ok) {
      console.warn(`Could not load logo from ${imageUrl}`);
      return null;
    }

    const blob = await response.blob();

    // Convert blob to base64
    const base64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

    // Kompres logo agar ukuran PDF tidak membengkak (logo hanya ~25mm di PDF)
    const compressed = await compressImage(base64, 300, 300, 0.8);
    return compressed || base64;
  } catch (error) {
    console.error('Error loading logo:', error);
    return null;
  }
};

/**
 * Generates a seminar minutes document with signatures
 * @param {Object} seminar - The seminar data
 * @param {Object} tugasAkhir - The related tugasAkhir data
 * @param {Array} evaluators - Array of evaluator objects with signatures
 * @param {String} type - Type of document ('berita_acara', 'form_penilaian')
 * @returns {Promise<Blob>} - PDF document as a Blob
 */
export const generateSeminarDocument = async (seminar, tugasAkhir, evaluators, type = 'berita_acara') => {
  // Initialize PDF document
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: [215.9, 330.2] // F4 size (215.9mm x 330.2mm)
  });

  // Get program studi from tugasAkhir or mahasiswa data, default to Rekayasa Kosmetik
  const programStudi = tugasAkhir?.prodi || seminar.mahasiswa?.prodi || 'REKAYASA KOSMETIK';

  // Load logo from public folder
  const logoBase64 = await loadLogoAsBase64('logoitera.png');

  // Kompres tanda tangan penguji/pembimbing agar ukuran PDF kecil
  evaluators = await compressEvaluatorSignatures(evaluators);

  // Add letterhead with dynamic program studi and logo
  await addLetterhead(pdf, programStudi, logoBase64);

  // Current date formatted (for document date reference if needed)
  const currentDate = format(new Date(), "d MMMM yyyy", { locale: id });

  // Format seminar date (full format with day name)
  const seminarDate = format(new Date(seminar.tanggal), "EEEE, d MMMM yyyy", { locale: id });

  // Format seminar date for signature (without day name)
  const seminarDateForSignature = format(new Date(seminar.tanggal), "d MMMM yyyy", { locale: id });

  // Calculate final score
  const finalScore = calculateFinalScore(seminar, evaluators);

  // Document title
  if (type === 'berita_acara') {
    addBeritaAcaraContent(pdf, seminar, tugasAkhir, evaluators, seminarDate, seminarDateForSignature, finalScore, programStudi);
  } else if (type === 'form_penilaian') {
    addFormPenilaianContent(pdf, seminar, tugasAkhir, evaluators, seminarDate, finalScore);
  }

  // Return PDF as blob
  return pdf.output('blob');
};

/**
 * Adds university letterhead to the PDF
 */
const addLetterhead = async (pdf, programStudi = 'REKAYASA KOSMETIK', logoBase64 = null) => {
  // Add university logo in top left corner
  if (logoBase64) {
    try {
      // Detect image format from base64 string
      let imageFormat = 'PNG'; // default
      if (logoBase64.startsWith('data:image/')) {
        const formatMatch = logoBase64.match(/data:image\/(\w+);base64/);
        if (formatMatch) {
          imageFormat = formatMatch[1].toUpperCase();
        }
      }

      // Logo position: left top, size: 25mm x 25mm
      // Adjust position and size as needed
      pdf.addImage(logoBase64, imageFormat, 20, 10, 25, 25);
    } catch (error) {
      console.error('Error adding logo to PDF:', error);
      // Continue without logo if there's an error
    }
  }

  // Add university name and address
  // Adjust text position if logo is added (shift right slightly)
  const textAlignX = logoBase64 ? 110 : 105; // Shift a bit right if logo exists

  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text('INSTITUT TEKNOLOGI SUMATERA', textAlignX, 20, { align: 'center' });

  pdf.setFontSize(14);
  pdf.text('FAKULTAS TEKNOLOGI INDUSTRI', textAlignX, 27, { align: 'center' });
  // Convert to title case instead of all uppercase
  const programStudiTitleCase = programStudi.split(' ').map(word =>
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  ).join(' ');
  pdf.text(`Program Studi ${programStudiTitleCase}`, textAlignX, 34, { align: 'center' });

  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Jl. Terusan Ryacudu, Way Huwi, Kec. Jati Agung, Kabupaten Lampung Selatan, Lampung 35365', textAlignX, 41, { align: 'center' });

  // Add horizontal line
  pdf.setLineWidth(0.5);
  pdf.line(20, 46, 190, 46);
  pdf.setLineWidth(1);
  pdf.line(20, 48, 190, 48);
};

/**
 * Adds content for seminar minutes (berita acara)
 */
const addBeritaAcaraContent = (pdf, seminar, tugasAkhir, evaluators, seminarDate, seminarDateForSignature, finalScore, programStudi) => {
  // Add document title
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');

  let docTitle = '';
  switch (seminar.jenis) {
    case 'proposal':
      docTitle = 'BERITA ACARA SEMINAR PROPOSAL TUGAS AKHIR';
      break;
    case 'hasil':
      docTitle = 'BERITA ACARA SEMINAR HASIL TUGAS AKHIR';
      break;
    case 'kompre':
      docTitle = 'BERITA ACARA SIDANG KOMPREHENSIF TUGAS AKHIR';
      break;
    default:
      docTitle = 'BERITA ACARA SEMINAR TUGAS AKHIR';
  }

  pdf.text(docTitle, 105, 60, { align: 'center' });

  // Add document number
  pdf.setFontSize(11);
  pdf.text(`Nomor: BA/${seminar.id.substring(0, 8)}/${new Date().getFullYear()}`, 105, 68, { align: 'center' });

  // Add content
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(11);

  let yPos = 80;

  // Format waktu dengan WIB
  let waktuText = '-';
  if (seminar.waktu) {
    // Parse waktu (format: HH:MM)
    const waktuParts = seminar.waktu.split(':');
    if (waktuParts.length >= 2) {
      const jam = waktuParts[0].padStart(2, '0');
      const menit = waktuParts[1].padStart(2, '0');
      waktuText = `${jam}.${menit} WIB`;
    } else {
      waktuText = `${seminar.waktu} WIB`;
    }
  }

  pdf.text('Pada hari ini:', 20, yPos);
  pdf.text(seminarDate, 70, yPos);

  yPos += 7;
  pdf.text('Pukul:', 20, yPos);
  pdf.text(waktuText, 70, yPos);

  yPos += 7;
  pdf.text('Tempat:', 20, yPos);
  pdf.text(seminar.tempat || '-', 70, yPos);

  yPos += 7;
  pdf.text('Telah dilaksanakan:', 20, yPos);

  yPos += 7;
  let seminarJenis = '';
  switch (seminar.jenis) {
    case 'proposal':
      seminarJenis = 'Seminar Proposal Tugas Akhir';
      break;
    case 'hasil':
      seminarJenis = 'Seminar Hasil Tugas Akhir';
      break;
    case 'kompre':
      seminarJenis = 'Sidang Komprehensif Tugas Akhir';
      break;
    default:
      seminarJenis = 'Seminar Tugas Akhir';
  }

  pdf.text(`${seminarJenis} dari mahasiswa:`, 70, yPos);

  // Student details
  yPos += 10;
  pdf.text('Nama', 20, yPos);
  pdf.text(':', 70, yPos);
  pdf.text(seminar.mahasiswaNama || '-', 75, yPos);

  yPos += 7;
  pdf.text('NIM', 20, yPos);
  pdf.text(':', 70, yPos);
  pdf.text(seminar.mahasiswaNIM || '-', 75, yPos);

  yPos += 7;
  pdf.text('Program Studi', 20, yPos);
  pdf.text(':', 70, yPos);
  pdf.text(programStudi || tugasAkhir?.prodi || '-', 75, yPos);

  yPos += 7;
  pdf.text('Judul Tugas Akhir', 20, yPos);
  pdf.text(':', 70, yPos);

  // Handle multi-line title
  const title = tugasAkhir.judul || seminar.judulTA || '-';
  const splitTitle = pdf.splitTextToSize(title, 110);
  pdf.text(splitTitle, 75, yPos);

  yPos += splitTitle.length * 7 + 5;

  // Skip listing committee members to save space
  yPos += 5;

  // Results
  const isPassed = finalScore >= 60;

  pdf.text('Hasil Penilaian:', 20, yPos);
  yPos += 7;
  pdf.text('Nilai Akhir', 30, yPos);
  pdf.text(`: ${finalScore.toFixed(2)}`, 70, yPos);

  yPos += 7;
  pdf.text('Status', 30, yPos);
  pdf.text(`: ${isPassed ? 'LULUS' : 'TIDAK LULUS'}`, 70, yPos);

  // Add catatan masukan dari penguji/pembimbing
  if (seminar.nilai && typeof seminar.nilai === 'object') {
    const allCatatanMasukan = [];

    // Collect catatan masukan from all evaluators
    evaluators.forEach(evaluator => {
      const evaluatorNilai = seminar.nilai[evaluator.id];
      if (evaluatorNilai && evaluatorNilai.catatanMasukan &&
        Array.isArray(evaluatorNilai.catatanMasukan) &&
        evaluatorNilai.catatanMasukan.length > 0) {
        evaluatorNilai.catatanMasukan.forEach((catatan, idx) => {
          if (catatan && catatan.trim() !== '') {
            allCatatanMasukan.push({
              evaluator: evaluator.nama,
              role: evaluator.role,
              catatan: catatan
            });
          }
        });
      }
    });

    // Add catatan masukan to PDF if any exist
    if (allCatatanMasukan.length > 0) {
      yPos += 10;
      pdf.setFont('helvetica', 'bold');
      pdf.text('Catatan Masukan dari Penguji/Pembimbing:', 20, yPos);
      pdf.setFont('helvetica', 'normal');

      yPos += 7;
      allCatatanMasukan.forEach((item, idx) => {
        // Check if we need a new page
        if (yPos > 280) {
          pdf.addPage();
          yPos = 30;
        }

        const catatanText = `${idx + 1}. [${item.evaluator} - ${item.role}] ${item.catatan}`;
        const splitCatatan = pdf.splitTextToSize(catatanText, 170);
        pdf.text(splitCatatan, 25, yPos);
        yPos += splitCatatan.length * 6 + 3;
      });
      yPos += 5;
    }
  }

  // Add additional notes if needed (catatan umum seminar)
  if (seminar.catatan) {
    yPos += 10;
    pdf.setFont('helvetica', 'bold');
    pdf.text('Catatan:', 20, yPos);
    pdf.setFont('helvetica', 'normal');

    const notes = pdf.splitTextToSize(seminar.catatan, 160);
    pdf.text(notes, 30, yPos + 7);

    yPos += notes.length * 7 + 10;
  } else {
    yPos += 15;
  }

  // Check if we need to add a new page for signatures
  // If we're already far down the page, add a new page
  if (yPos > 250) {
    pdf.addPage();
    yPos = 30; // Reset position to top of new page
  }

  // Add signature section - use seminar date instead of current date
  pdf.text('Lampung Selatan, ' + seminarDateForSignature, 130, yPos);
  yPos += 7;
  pdf.text('Komite Penguji,', 130, yPos);

  yPos += 12; // Reduced from 15 to 12

  // Add signature images
  addSignatures(pdf, evaluators, yPos);
};

/**
 * Adds content for the evaluation form
 */
const addFormPenilaianContent = (pdf, seminar, tugasAkhir, evaluators, seminarDate, finalScore) => {
  // Add document title
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');

  let docTitle = '';
  switch (seminar.jenis) {
    case 'proposal':
      docTitle = 'FORM PENILAIAN SEMINAR PROPOSAL TUGAS AKHIR';
      break;
    case 'hasil':
      docTitle = 'FORM PENILAIAN SEMINAR HASIL TUGAS AKHIR';
      break;
    case 'kompre':
      docTitle = 'FORM PENILAIAN SIDANG KOMPREHENSIF TUGAS AKHIR';
      break;
    default:
      docTitle = 'FORM PENILAIAN SEMINAR TUGAS AKHIR';
  }

  pdf.text(docTitle, 105, 60, { align: 'center' });

  // Add student info
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'normal');

  let yPos = 75;

  pdf.text('Nama Mahasiswa:', 20, yPos);
  pdf.text(seminar.mahasiswaNama || '-', 70, yPos);

  yPos += 7;
  pdf.text('NIM:', 20, yPos);
  pdf.text(seminar.mahasiswaNIM || '-', 70, yPos);

  yPos += 7;
  pdf.text('Judul Tugas Akhir:', 20, yPos);
  const title = tugasAkhir.judul || seminar.judulTA || '-';
  const splitTitle = pdf.splitTextToSize(title, 120);
  pdf.text(splitTitle, 70, yPos);

  yPos += splitTitle.length * 7 + 5;

  pdf.text('Tanggal Seminar:', 20, yPos);
  pdf.text(seminarDate, 70, yPos);

  yPos += 10;

  // Add evaluation criteria table
  if (seminar.kriteriaPenilaian && seminar.kriteriaPenilaian.length > 0) {
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text('KRITERIA PENILAIAN', 105, yPos, { align: 'center' });

    yPos += 8;

    // Create table headers
    const tableHeaders = [
      'No',
      'Nama Penguji (Peran)',
      'Nilai Rata-rata',
      'Bobot (%)',
      'Nilai Akhir'
    ];

    // Create table body
    const tableBody = [];

    // Sort evaluators by role
    const sortedEvaluators = [...evaluators].sort((a, b) => {
      const roleOrder = {
        'Pembimbing 1': 0,
        'Pembimbing 2': 1,
        'Penguji 1': 2,
        'Penguji 2': 3
      };
      return roleOrder[a.role] - roleOrder[b.role];
    });

    // Add rows for each evaluator
    sortedEvaluators.forEach((evaluator, index) => {
      // Calculate average score for this evaluator
      let totalScore = 0;
      let count = 0;

      if (seminar.nilai && seminar.nilai[evaluator.id]) {
        const pengujiNilai = seminar.nilai[evaluator.id];

        seminar.kriteriaPenilaian.forEach(kriteria => {
          if (pengujiNilai[kriteria.id]) {
            // Unweighted score for average calculation
            // We want the raw score from 0-100 here to average effectively
            // But wait, the previous logic calculated weighted total based on criteria weights
            // Let's check how "Komponen Nilai" should be calculated.
            // Usually it's the average of scores or the sum of (score * criteria_weight / 100)

            // Let's use the sum of weighted criteria scores which gives the "Total Score" for that evaluator (0-100 scale)
            const kriteriaScore = pengujiNilai[kriteria.id];
            const kriteriaBobot = kriteria.bobot;
            totalScore += kriteriaScore * (kriteriaBobot / 100);
            count++;
          }
        });
      }

      // Determine evaluator weight
      let weight = 0;
      switch (evaluator.role) {
        case 'Pembimbing 1': weight = 0.35; break;
        case 'Pembimbing 2': weight = 0.25; break;
        case 'Penguji 1': weight = 0.20; break;
        case 'Penguji 2': weight = 0.20; break;
        default: weight = 0.20;
      }

      // Calculate contribution
      const score = count > 0 ? totalScore : 0;
      const contribution = score * weight;

      const row = [
        (index + 1).toString(),
        `${evaluator.nama}\n(${evaluator.role})`,
        score.toFixed(2),
        `${(weight * 100).toFixed(0)}%`,
        contribution.toFixed(2)
      ];

      tableBody.push(row);
    });

    // Add total row
    const totalRow = [
      '',
      '',
      '',
      'TOTAL NILAI AKHIR',
      finalScore.toFixed(2)
    ];

    tableBody.push(totalRow);

    // Add the table
    autoTable(pdf, {
      head: [tableHeaders],
      body: tableBody,
      startY: yPos,
      theme: 'grid',
      styles: {
        fontSize: 10,
        cellPadding: 4,
        valign: 'middle'
      },
      headStyles: {
        fillColor: [66, 135, 245],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        halign: 'center'
      },
      columnStyles: {
        0: { cellWidth: 10, halign: 'center' }, // No
        1: { cellWidth: 90 }, // Nama Penguji
        2: { cellWidth: 30, halign: 'center' }, // Nilai Rata-rata
        3: { cellWidth: 25, halign: 'center' }, // Bobot
        4: { cellWidth: 35, halign: 'center', fontStyle: 'bold' }  // Nilai Akhir
      },
      didDrawPage: (data) => {
        // Update yPos after the table is drawn
        yPos = data.cursor.y + 15;
      }
    });

    // Add result status
    const isPassed = finalScore >= 60;

    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`Status: ${isPassed ? 'LULUS' : 'TIDAK LULUS'}`, 105, yPos, { align: 'center' });

    yPos += 15;

    // Add catatan masukan dari penguji/pembimbing
    if (seminar.nilai && typeof seminar.nilai === 'object') {
      const allCatatanMasukan = [];

      // Collect catatan masukan from all evaluators
      evaluators.forEach(evaluator => {
        const evaluatorNilai = seminar.nilai[evaluator.id];
        if (evaluatorNilai && evaluatorNilai.catatanMasukan &&
          Array.isArray(evaluatorNilai.catatanMasukan) &&
          evaluatorNilai.catatanMasukan.length > 0) {
          evaluatorNilai.catatanMasukan.forEach((catatan) => {
            if (catatan && catatan.trim() !== '') {
              allCatatanMasukan.push({
                evaluator: evaluator.nama,
                role: evaluator.role,
                catatan: catatan
              });
            }
          });
        }
      });

      // Add catatan masukan to PDF if any exist
      if (allCatatanMasukan.length > 0) {
        // Check if we need a new page before adding catatan
        if (yPos > 270) {
          pdf.addPage();
          yPos = 30;
        }

        yPos += 5;
        pdf.setFont('helvetica', 'bold');
        pdf.text('Catatan Masukan dari Penguji/Pembimbing:', 20, yPos);
        pdf.setFont('helvetica', 'normal');

        yPos += 7;
        allCatatanMasukan.forEach((item, idx) => {
          // Check if we need a new page
          if (yPos > 280) {
            pdf.addPage();
            yPos = 30;
          }

          const catatanText = `${idx + 1}. [${item.evaluator} - ${item.role}] ${item.catatan}`;
          const splitCatatan = pdf.splitTextToSize(catatanText, 170);
          pdf.text(splitCatatan, 25, yPos);
          yPos += splitCatatan.length * 6 + 3;
        });
        yPos += 5;
      }
    }

    // Check if we need to add a new page for signatures
    // If we're already far down the page, add a new page
    if (yPos > 250) {
      pdf.addPage();
      yPos = 30; // Reset position to top of new page
    }

    // Add signature section for all evaluators
    pdf.setFont('helvetica', 'normal');
    pdf.text('Tanda Tangan Penguji:', 20, yPos);

    yPos += 8;

    // Add signature images (2x2 grid) - more compact size
    const sigWidth = 80;
    const sigHeight = 25; // Reduced from 30 to 25

    let leftCol = 30;
    let rightCol = 120;

    // Define positions for all 4 possible signatures - more compact spacing
    const spacing = 20; // Reduced from 25 to 20
    const positions = [
      { x: leftCol, y: yPos, role: 'Pembimbing 1' },
      { x: rightCol, y: yPos, role: 'Pembimbing 2' },
      { x: leftCol, y: yPos + sigHeight + spacing, role: 'Penguji 1' },
      { x: rightCol, y: yPos + sigHeight + spacing, role: 'Penguji 2' }
    ];

    // Sort evaluators by role for consistent placement
    const evaluatorsForSignature = [...evaluators].sort((a, b) => {
      // Order: Pembimbing 1, Pembimbing 2, Penguji 1, Penguji 2
      const roleOrder = {
        'Pembimbing 1': 0,
        'Pembimbing 2': 1,
        'Penguji 1': 2,
        'Penguji 2': 3
      };
      return roleOrder[a.role] - roleOrder[b.role];
    });

    const signaturePositions = {
      'Pembimbing 1': { x: leftCol, y: yPos },
      'Pembimbing 2': { x: rightCol, y: yPos },
      'Penguji 1': { x: leftCol, y: yPos + sigHeight + spacing },
      'Penguji 2': { x: rightCol, y: yPos + sigHeight + spacing }
    };

    // Create a mapping of roles to evaluators
    const roleMap = {};
    evaluatorsForSignature.forEach(evaluator => {
      roleMap[evaluator.role] = evaluator;
    });

    // Add all 4 signature spots whether we have evaluators for them or not
    positions.forEach(position => {
      const { x, y, role } = position;
      const evaluator = roleMap[role];

      // Always show the role title - smaller font
      pdf.setFontSize(9);
      pdf.text(role, x + sigWidth / 2, y - 4, { align: 'center' });

      if (evaluator && evaluator.signatureURL) {
        // Add signature image, more compact size
        const isBase64 = evaluator.signatureURL.startsWith('data:image');
        const sigWidthAdjusted = 45; // Reduced from 50 to 45
        pdf.addImage(evaluator.signatureURL, isBase64 ? 'JPEG' : 'PNG', x + (sigWidth - sigWidthAdjusted) / 2, y, sigWidthAdjusted, sigHeight);

        // Add name and NIP - smaller font and tighter spacing
        pdf.setFontSize(9);
        pdf.text(evaluator.nama, x + sigWidth / 2, y + sigHeight + 4, { align: 'center' });
        pdf.setFontSize(8);
        pdf.text('NIP. ' + (evaluator.nip || '-'), x + sigWidth / 2, y + sigHeight + 8, { align: 'center' });
      } else {
        // If no evaluator or no signature, show placeholder
        pdf.setFontSize(9);
        pdf.text('_______________________', x + sigWidth / 2, y + sigHeight / 2, { align: 'center' });
        pdf.setFontSize(8);
        pdf.text('NIP. __________________', x + sigWidth / 2, y + sigHeight + 8, { align: 'center' });
      }

      // Reset font size
      pdf.setFontSize(11);
    });
  }
};

/**
 * Adds signature images to the PDF
 */
const addSignatures = (pdf, evaluators, startY) => {
  if (evaluators.length === 0) return;

  // Add signature images (2x2 grid) - more compact size
  const sigWidth = 80;
  const sigHeight = 25; // Reduced from 30 to 25

  let leftCol = 30;
  let rightCol = 120;

  // Define positions for all 4 possible signatures - more compact spacing
  const spacing = 20; // Reduced from 25 to 20
  const positions = [
    { x: leftCol, y: startY, role: 'Pembimbing 1' },
    { x: rightCol, y: startY, role: 'Pembimbing 2' },
    { x: leftCol, y: startY + sigHeight + spacing, role: 'Penguji 1' },
    { x: rightCol, y: startY + sigHeight + spacing, role: 'Penguji 2' }
  ];

  // Sort evaluators by role for consistent placement
  const sortedEvaluators = [...evaluators].sort((a, b) => {
    // Order: Pembimbing 1, Pembimbing 2, Penguji 1, Penguji 2
    const roleOrder = {
      'Pembimbing 1': 0,
      'Pembimbing 2': 1,
      'Penguji 1': 2,
      'Penguji 2': 3
    };

    return roleOrder[a.role] - roleOrder[b.role];
  });

  // Create a mapping of roles to evaluators
  const roleMap = {};
  sortedEvaluators.forEach(evaluator => {
    roleMap[evaluator.role] = evaluator;
  });

  // Add all 4 signature spots whether we have evaluators for them or not
  positions.forEach(position => {
    const { x, y, role } = position;
    const evaluator = roleMap[role];

    // Always show the role title - smaller font
    pdf.setFontSize(9);
    pdf.text(role, x + sigWidth / 2, y - 4, { align: 'center' });

    if (evaluator && evaluator.signatureURL) {
      // Add signature image, more compact size
      const isBase64 = evaluator.signatureURL.startsWith('data:image');
      const sigWidthAdjusted = 45; // Reduced from 50 to 45
      pdf.addImage(evaluator.signatureURL, isBase64 ? 'JPEG' : 'PNG', x + (sigWidth - sigWidthAdjusted) / 2, y, sigWidthAdjusted, sigHeight);

      // Add name and NIP - smaller font and tighter spacing
      pdf.setFontSize(9);
      pdf.text(evaluator.nama, x + sigWidth / 2, y + sigHeight + 4, { align: 'center' });
      pdf.setFontSize(8);
      pdf.text('NIP. ' + (evaluator.nip || '-'), x + sigWidth / 2, y + sigHeight + 8, { align: 'center' });
    } else {
      // If no evaluator or no signature, show placeholder
      pdf.setFontSize(9);
      pdf.text('_______________________', x + sigWidth / 2, y + sigHeight / 2, { align: 'center' });
      pdf.setFontSize(8);
      pdf.text('NIP. __________________', x + sigWidth / 2, y + sigHeight + 8, { align: 'center' });
    }

    // Reset font size
    pdf.setFontSize(11);
  });
};

/**
 * Calculates final score from evaluator scores
 * If a manual score is provided, uses that instead
 */
const calculateFinalScore = (seminar, evaluators) => {
  // If the seminar has a manually calculated score, use that instead
  if (seminar.manualFinalScore !== undefined) {
    return seminar.manualFinalScore;
  }

  // Otherwise calculate using the standard algorithm
  if (!seminar.nilai || Object.keys(seminar.nilai).length === 0) {
    return 0;
  }

  // Extract pembimbing IDs for role identification
  const pembimbingIds = [];
  if (seminar.tugasAkhir && Array.isArray(seminar.tugasAkhir.pembimbingId)) {
    pembimbingIds.push(...seminar.tugasAkhir.pembimbingId.filter(id => typeof id === 'string'));
  }

  // Get all evaluator IDs
  const pengujiIds = Object.keys(seminar.nilai);

  // Track assigned roles
  const assignedRoles = {
    'Pembimbing 1': false,
    'Pembimbing 2': false,
    'Penguji 1': false,
    'Penguji 2': false
  };

  // First assign pembimbing roles
  const evaluatorScores = [];
  pengujiIds.forEach(pengujiId => {
    const pembimbingIndex = pembimbingIds.indexOf(pengujiId);

    if (pembimbingIndex === 0) {
      evaluatorScores.push({
        id: pengujiId,
        role: 'Pembimbing 1',
        weight: 0.35
      });
      assignedRoles['Pembimbing 1'] = true;
    } else if (pembimbingIndex === 1) {
      evaluatorScores.push({
        id: pengujiId,
        role: 'Pembimbing 2',
        weight: 0.25
      });
      assignedRoles['Pembimbing 2'] = true;
    }
  });

  // Then assign remaining penguji roles
  pengujiIds.forEach(pengujiId => {
    // Skip if already assigned as pembimbing
    if (evaluatorScores.some(e => e.id === pengujiId)) return;

    // Assign as penguji 1 or 2
    if (!assignedRoles['Penguji 1']) {
      evaluatorScores.push({
        id: pengujiId,
        role: 'Penguji 1',
        weight: 0.20
      });
      assignedRoles['Penguji 1'] = true;
    } else if (!assignedRoles['Penguji 2']) {
      evaluatorScores.push({
        id: pengujiId,
        role: 'Penguji 2',
        weight: 0.20
      });
      assignedRoles['Penguji 2'] = true;
    } else {
      // If we have extra evaluators, add default weight
      evaluatorScores.push({
        id: pengujiId,
        role: `Penguji Extra`,
        weight: 0.20
      });
    }
  });

  // Calculate individual scores for each evaluator
  let totalScore = 0;
  let totalWeight = 0;

  // Process each evaluator
  evaluatorScores.forEach(evaluator => {
    const pengujiNilai = seminar.nilai[evaluator.id];
    if (!pengujiNilai) {
      return;
    }

    // Calculate score for this evaluator
    let total = 0;
    let count = 0;

    // Process all criteria
    if (Array.isArray(seminar.kriteriaPenilaian)) {
      seminar.kriteriaPenilaian.forEach(kriteria => {
        if (kriteria && kriteria.id && pengujiNilai[kriteria.id]) {
          const nilai = pengujiNilai[kriteria.id];
          const bobot = kriteria.bobot / 100;
          const weightedScore = nilai * bobot;

          total += weightedScore;
          count++;
        }
      });
    }

    // Save the score
    const score = count > 0 ? total : 0;

    // Add to total
    totalScore += score * evaluator.weight;
    totalWeight += evaluator.weight;
  });

  // Normalize if not all evaluators submitted scores
  if (totalWeight > 0 && totalWeight < 1) {
    totalScore = totalScore / totalWeight;
  }

  return totalScore;
};

/**
 * Konversi nilai angka ke nilai mutu (huruf) sesuai aturan:
 * >=75 A, >=70 AB, >=65 B, >=60 BC, >=50 C, >=40 D, selain itu E
 * @param {number} nilai - Nilai akhir (0-100)
 * @returns {string} - Nilai mutu (A/AB/B/BC/C/D/E)
 */
export const getNilaiMutu = (nilai) => {
  const n = Number(nilai) || 0;
  if (n >= 75) return 'A';
  if (n >= 70) return 'AB';
  if (n >= 65) return 'B';
  if (n >= 60) return 'BC';
  if (n >= 50) return 'C';
  if (n >= 40) return 'D';
  return 'E';
};

/**
 * Generate Berita Acara Nilai Akhir (gabungan) Tugas Akhir.
 * Merekap nilai Seminar Proposal (30%), Seminar Hasil (40%), dan Sidang
 * Komprehensif (30%), menghitung nilai akhir dan nilai mutu, lalu
 * ditandatangani oleh seluruh pembimbing & penguji dan diketahui oleh
 * Koordinator Tugas Akhir.
 *
 * @param {Object} data
 * @param {Object} data.seminar - Data seminar acuan (dipakai untuk identitas mahasiswa)
 * @param {Object} data.tugasAkhir - Data tugas akhir
 * @param {Array} data.evaluators - Array penguji/pembimbing dengan signatureURL
 * @param {Object} data.komponenNilai - { proposal, hasil, kompre } nilai tiap tahap (0-100)
 * @returns {Promise<Blob>} - PDF document as a Blob
 */
export const generateBeritaAcaraNilaiAkhir = async ({ seminar, tugasAkhir, evaluators = [], komponenNilai = {} }) => {
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: [215.9, 330.2] // F4 size
  });

  const programStudi = tugasAkhir?.prodi || seminar.mahasiswa?.prodi || 'REKAYASA KOSMETIK';
  const logoBase64 = await loadLogoAsBase64('logoitera.png');

  // Kompres tanda tangan pembimbing/penguji agar ukuran PDF kecil
  evaluators = await compressEvaluatorSignatures(evaluators);

  await addLetterhead(pdf, programStudi, logoBase64);

  // Proporsi bobot nilai akhir
  const bobot = { proposal: 0.30, hasil: 0.40, kompre: 0.30 };

  // Ambil nilai tiap komponen (null jika belum ada)
  const nProposal = komponenNilai.proposal;
  const nHasil = komponenNilai.hasil;
  const nKompre = komponenNilai.kompre;

  // Hitung nilai akhir terbobot
  const nilaiAkhir =
    (Number(nProposal) || 0) * bobot.proposal +
    (Number(nHasil) || 0) * bobot.hasil +
    (Number(nKompre) || 0) * bobot.kompre;

  const nilaiMutu = getNilaiMutu(nilaiAkhir);
  const isLulus = nilaiAkhir >= 60;

  // Tanggal berita acara = tanggal pelaksanaan Sidang Komprehensif
  const tanggalBeritaAcara = seminar.tanggal
    ? format(new Date(seminar.tanggal), "d MMMM yyyy", { locale: id })
    : format(new Date(), "d MMMM yyyy", { locale: id });

  // Judul dokumen
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text('BERITA ACARA NILAI AKHIR TUGAS AKHIR', 105, 60, { align: 'center' });

  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Nomor: BA-NA/${(seminar.tugasAkhirId || seminar.id || '').substring(0, 8)}/${new Date().getFullYear()}`, 105, 68, { align: 'center' });

  let yPos = 80;

  // Identitas mahasiswa
  const labelX = 20;
  const colonX = 65;
  const valueX = 70;

  pdf.text('Nama', labelX, yPos);
  pdf.text(':', colonX, yPos);
  pdf.text(seminar.mahasiswaNama || '-', valueX, yPos);

  yPos += 7;
  pdf.text('NIM', labelX, yPos);
  pdf.text(':', colonX, yPos);
  pdf.text(seminar.mahasiswaNIM || '-', valueX, yPos);

  yPos += 7;
  pdf.text('Program Studi', labelX, yPos);
  pdf.text(':', colonX, yPos);
  const programStudiTitleCase = programStudi.split(' ').map(word =>
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  ).join(' ');
  pdf.text(programStudiTitleCase, valueX, yPos);

  yPos += 7;
  pdf.text('Judul Tugas Akhir', labelX, yPos);
  pdf.text(':', colonX, yPos);
  const title = tugasAkhir?.judul || seminar.judulTA || '-';
  const splitTitle = pdf.splitTextToSize(title, 120);
  pdf.text(splitTitle, valueX, yPos);
  yPos += splitTitle.length * 6 + 6;

  // Kalimat pengantar
  pdf.text('Berdasarkan hasil penilaian pada setiap tahapan Tugas Akhir, diperoleh rekapitulasi', labelX, yPos);
  yPos += 6;
  pdf.text('nilai sebagai berikut:', labelX, yPos);
  yPos += 6;

  // Tabel rekap nilai
  const fmt = (v) => (v === null || v === undefined || v === '' || isNaN(Number(v))) ? '-' : Number(v).toFixed(2);

  const tableBody = [
    ['1', 'Seminar Proposal', fmt(nProposal), '30%', fmt((Number(nProposal) || 0) * bobot.proposal)],
    ['2', 'Seminar Hasil', fmt(nHasil), '40%', fmt((Number(nHasil) || 0) * bobot.hasil)],
    ['3', 'Sidang Komprehensif', fmt(nKompre), '30%', fmt((Number(nKompre) || 0) * bobot.kompre)]
  ];

  autoTable(pdf, {
    head: [['No', 'Komponen Penilaian', 'Nilai', 'Bobot', 'Nilai Terbobot']],
    body: tableBody,
    foot: [
      ['', 'Nilai Akhir', '', '', nilaiAkhir.toFixed(2)],
      ['', 'Nilai Mutu', '', '', nilaiMutu]
    ],
    startY: yPos,
    theme: 'grid',
    styles: { fontSize: 10, cellPadding: 3, valign: 'middle' },
    headStyles: { fillColor: [66, 135, 245], textColor: [255, 255, 255], fontStyle: 'bold', halign: 'center' },
    footStyles: { fillColor: [230, 230, 230], textColor: [0, 0, 0], fontStyle: 'bold' },
    columnStyles: {
      0: { cellWidth: 12, halign: 'center' },
      1: { cellWidth: 80 },
      2: { cellWidth: 25, halign: 'center' },
      3: { cellWidth: 20, halign: 'center' },
      4: { cellWidth: 33, halign: 'center' }
    },
    margin: { left: 20, right: 20 }
  });

  yPos = pdf.lastAutoTable.finalY + 10;

  // Status kelulusan
  pdf.setFont('helvetica', 'bold');
  pdf.text(`Status: ${isLulus ? 'LULUS' : 'TIDAK LULUS'}`, labelX, yPos);
  pdf.setFont('helvetica', 'normal');
  yPos += 6;
  pdf.setFontSize(9);
  pdf.text('Keterangan bobot: Seminar Proposal 30%, Seminar Hasil 40%, Sidang Komprehensif 30%.', labelX, yPos);
  yPos += 5;
  pdf.text('Nilai Mutu: A (>=75), AB (>=70), B (>=65), BC (>=60), C (>=50), D (>=40), E (<40).', labelX, yPos);
  pdf.setFontSize(11);
  yPos += 12;

  // Tanggal & tempat
  if (yPos > 230) {
    pdf.addPage();
    yPos = 30;
  }

  pdf.text('Lampung Selatan, ' + tanggalBeritaAcara, 130, yPos);
  yPos += 7;
  pdf.text('Tim Pembimbing dan Penguji,', 130, yPos);
  yPos += 12;

  // Tanda tangan seluruh pembimbing & penguji (grid 2x2)
  addSignatures(pdf, evaluators, yPos);

  // Posisi setelah blok tanda tangan (2 baris grid)
  yPos = yPos + (25 + 20) * 2 + 5;

  if (yPos > 285) {
    pdf.addPage();
    yPos = 30;
  }

  // Diketahui oleh Koordinator Tugas Akhir
  const koordinator = await getKoordinatorTugasAkhir();
  koordinator.signatureURL = await compressImage(koordinator.signatureURL, 400, 200, 0.7);

  pdf.setFont('helvetica', 'normal');
  pdf.text('Mengetahui,', 105, yPos, { align: 'center' });
  yPos += 6;
  pdf.setFont('helvetica', 'bold');
  pdf.text('Koordinator Tugas Akhir', 105, yPos, { align: 'center' });
  yPos += 5;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.text(`Program Studi ${programStudiTitleCase}`, 105, yPos, { align: 'center' });
  pdf.setFontSize(11);
  yPos += 8;

  const koordinatorSig = await compressImage(koordinator.signatureURL, 400, 200, 0.7);
  if (koordinatorSig) {
    try {
      pdf.addImage(koordinatorSig, 'JPEG', 85, yPos, 40, 20);
      yPos += 23;
    } catch (sigError) {
      console.error('Error adding koordinator signature:', sigError);
      yPos += 20;
    }
  } else {
    yPos += 20;
  }

  pdf.text(koordinator.nama || 'Koordinator Tugas Akhir', 105, yPos, { align: 'center' });
  yPos += 5;
  pdf.setFontSize(10);
  pdf.text(`NIP. ${koordinator.nip || '-'}`, 105, yPos, { align: 'center' });

  return pdf.output('blob');
};

/**
 * Get koordinator tugas akhir dari settings
 */
const getKoordinatorTugasAkhir = async () => {
  try {
    // Cek di collection settings
    const settingsDoc = await getDoc(doc(db, "settings", "koordinatorTA"));
    if (settingsDoc.exists()) {
      const settingsData = settingsDoc.data();
      if (settingsData.koordinatorId) {
        // Fetch data koordinator
        const koordinatorDoc = await getDoc(doc(db, "users", settingsData.koordinatorId));
        if (koordinatorDoc.exists()) {
          const koordinatorData = koordinatorDoc.data();
          return {
            nama: koordinatorData.nama || 'Koordinator Tugas Akhir',
            nip: koordinatorData.nip || '-',
            signatureURL: koordinatorData.signatureURL || null
          };
        }
      }
    }

    // Fallback jika tidak ada koordinator yang di-set
    return {
      nama: 'Koordinator Tugas Akhir',
      nip: '-',
      signatureURL: null
    };
  } catch (error) {
    console.error('Error fetching koordinator:', error);
    return {
      nama: 'Koordinator Tugas Akhir',
      nip: '-',
      signatureURL: null
    };
  }
};

/**
 * Generate PDF undangan untuk pembimbing/penguji
 * @param {Object} seminar - Data seminar
 * @param {Object} tugasAkhir - Data tugas akhir
 * @param {Object} recipient - Data penerima undangan (pembimbing/penguji)
 * @param {String} role - Role penerima (Pembimbing 1, Pembimbing 2, Penguji 1, Penguji 2)
 * @returns {Promise<Blob>} - PDF document as a Blob
 */
export const generateUndanganPDF = async (seminar, tugasAkhir, recipient, role) => {
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: [215.9, 330.2] // F4 size
  });

  const programStudiRaw = tugasAkhir?.prodi || seminar.mahasiswa?.prodi || 'REKAYASA KOSMETIK';
  // Convert to title case (not all uppercase)
  const programStudiTitleCase = programStudiRaw.split(' ').map(word =>
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  ).join(' ');
  const logoBase64 = await loadLogoAsBase64('logoitera.png');

  // Add letterhead (use raw for header which will be converted in addLetterhead)
  await addLetterhead(pdf, programStudiRaw, logoBase64);

  // Format dates
  const currentDate = format(new Date(), "d MMMM yyyy", { locale: id });
  const seminarDate = format(new Date(seminar.tanggal), "EEEE, d MMMM yyyy", { locale: id });

  // Get koordinator data
  const koordinator = await getKoordinatorTugasAkhir();
  koordinator.signatureURL = await compressImage(koordinator.signatureURL, 400, 200, 0.7);

  let yPos = 60;

  // Document title
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text('UNDANGAN', 105, yPos, { align: 'center' });

  let seminarType = '';
  switch (seminar.jenis) {
    case 'proposal':
      seminarType = 'Seminar Proposal Tugas Akhir';
      break;
    case 'hasil':
      seminarType = 'Seminar Hasil Tugas Akhir';
      break;
    case 'kompre':
      seminarType = 'Sidang Komprehensif Tugas Akhir';
      break;
  }

  yPos += 12;
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'normal');
  pdf.text(seminarType, 105, yPos, { align: 'center' });

  yPos += 15; // Reduced from 20
  pdf.setFontSize(11);

  // Recipient info - langsung nama saja tanpa role
  pdf.text('Kepada Yth.', 20, yPos);
  yPos += 6; // Reduced from 8
  pdf.setFont('helvetica', 'normal');
  pdf.text(recipient.nama || 'Dosen', 20, yPos);

  yPos += 7; // Reduced from 10
  pdf.text('Di Tempat', 20, yPos);

  yPos += 10; // Reduced from 15
  pdf.text('Dengan hormat,', 20, yPos);

  yPos += 8; // Reduced from 12

  // Body text dengan format yang lebih rapi
  pdf.setFontSize(11);

  // Paragraph 1
  pdf.text(`Sehubungan dengan rencana pelaksanaan ${seminarType.toLowerCase()} yang akan dilaksanakan oleh:`, 20, yPos);
  yPos += 8; // Reduced from 10

  // Student info dengan indent - gunakan fixed width untuk alignment titik dua
  const labelWidth = 70; // Fixed width untuk label agar titik dua sejajar
  pdf.text('Nama', 25, yPos);
  pdf.text(':', labelWidth, yPos);
  pdf.text(seminar.mahasiswaNama || '-', labelWidth + 3, yPos);
  yPos += 6; // Reduced from 7
  pdf.text('NIM', 25, yPos);
  pdf.text(':', labelWidth, yPos);
  pdf.text(seminar.mahasiswaNIM || '-', labelWidth + 3, yPos);
  yPos += 6; // Reduced from 7
  pdf.text('Judul TA', 25, yPos);
  pdf.text(':', labelWidth, yPos);
  const title = tugasAkhir?.judul || seminar.judulTA || '-';
  // F4 width: 215.9mm, margin kiri: 25mm, margin kanan: 25mm (untuk keamanan)
  // Label width: 70mm, spacing: 3mm
  // Max text width: 215.9 - 25 - 25 = 165.9mm
  // Available for title: 165.9 - 70 - 3 = ~93mm
  const titleWidth = 85; // Lebih kecil lagi untuk memastikan tidak menembus margin kanan
  const splitTitle = pdf.splitTextToSize(title, titleWidth);
  pdf.text(splitTitle, labelWidth + 3, yPos);
  yPos += splitTitle.length * 4 + 3; // Reduced spacing

  yPos += 3; // Reduced from 5
  pdf.text('Maka dengan ini kami mengundang Bapak/Ibu untuk dapat hadir pada:', 20, yPos);
  yPos += 8; // Reduced from 10

  // Event details dengan indent - gunakan fixed width untuk alignment
  pdf.text('Hari/Tanggal', 25, yPos);
  pdf.text(':', labelWidth, yPos);
  pdf.text(seminarDate, labelWidth + 3, yPos);
  yPos += 6; // Reduced from 7
  const waktuText = seminar.waktu ? (seminar.waktu.includes('WIB') ? seminar.waktu : `${seminar.waktu} WIB`) : '09:00 WIB';
  pdf.text('Waktu', 25, yPos);
  pdf.text(':', labelWidth, yPos);
  pdf.text(waktuText, labelWidth + 3, yPos);
  yPos += 6; // Reduced from 7
  pdf.text('Tempat', 25, yPos);
  pdf.text(':', labelWidth, yPos);
  pdf.text(seminar.tempat || '-', labelWidth + 3, yPos);

  yPos += 7; // Reduced from 10
  pdf.text(`Sebagai ${role} dalam kegiatan tersebut.`, 20, yPos);

  yPos += 7; // Reduced from 10
  pdf.text('Demikian undangan ini kami sampaikan. Atas kehadiran dan perhatiannya, kami ucapkan terima kasih.', 20, yPos);

  yPos += 12; // Reduced from 20 - no page break needed, keep everything on one page

  // Signature section - dengan tanda tangan koordinator (compact version)
  pdf.text('Hormat kami,', 20, yPos);
  yPos += 8; // Reduced from 12
  pdf.setFont('helvetica', 'bold');
  pdf.text('Koordinator Tugas Akhir', 20, yPos);
  yPos += 5; // Reduced from 7
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.text(`Program Studi ${programStudiTitleCase}`, 20, yPos);
  yPos += 12; // Reduced from 18

  // Add signature image if available - make it smaller
  if (koordinator.signatureURL) {
    try {
      const isBase64 = koordinator.signatureURL.startsWith('data:image');
      const sigHeight = 18; // Smaller signature (reduced from 25)
      const sigWidth = 40; // Smaller signature (reduced from 50)
      pdf.addImage(
        koordinator.signatureURL,
        isBase64 ? (koordinator.signatureURL.includes('png') ? 'PNG' : 'JPEG') : 'PNG',
        20,
        yPos,
        sigWidth,
        sigHeight
      );
      yPos += sigHeight + 4; // Reduced spacing
    } catch (sigError) {
      console.error('Error adding signature:', sigError);
      yPos += 4;
    }
  } else {
    yPos += 4;
  }

  // Nama dan NIP koordinator - ensure they are visible and not cut off
  pdf.setFontSize(11);
  const namaKoordinator = koordinator.nama || 'Koordinator';
  pdf.text(namaKoordinator, 20, yPos);
  yPos += 5; // Reduced from 6
  pdf.setFontSize(10);
  const nipKoordinator = koordinator.nip || '-';
  pdf.text(`NIP. ${nipKoordinator}`, 20, yPos);

  return pdf.output('blob');
};

/**
 * Generate PDF absensi menggunakan template yang ada
 * Untuk saat ini, kita akan membuat PDF baru dengan struktur yang sama seperti template
 * Template PDF di public/daftarhadir.pdf dapat digunakan sebagai referensi
 * @param {Object} seminar - Data seminar
 * @param {Object} tugasAkhir - Data tugas akhir
 * @returns {Promise<Blob>} - PDF document as a Blob
 */
export const generateAbsensiPDF = async (seminar, tugasAkhir) => {
  return generateAbsensiFromTemplate(seminar, tugasAkhir);
};

/**
 * Generate absensi PDF from template structure (fallback method)
 */
const generateAbsensiFromTemplate = async (seminar, tugasAkhir) => {
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: [215.9, 330.2] // F4 size
  });

  const programStudi = tugasAkhir?.prodi || seminar.mahasiswa?.prodi || 'REKAYASA KOSMETIK';
  const logoBase64 = await loadLogoAsBase64('logoitera.png');

  // Add letterhead
  await addLetterhead(pdf, programStudi, logoBase64);

  let yPos = 60;

  // Title
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text('DAFTAR HADIR', 105, yPos, { align: 'center' });

  let seminarType = '';
  switch (seminar.jenis) {
    case 'proposal':
      seminarType = 'Seminar Proposal Tugas Akhir';
      break;
    case 'hasil':
      seminarType = 'Seminar Hasil Tugas Akhir';
      break;
    case 'kompre':
      seminarType = 'Sidang Komprehensif Tugas Akhir';
      break;
  }

  yPos += 10;
  pdf.setFontSize(12);
  pdf.text(seminarType, 105, yPos, { align: 'center' });

  yPos += 15;
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'normal');

  // Seminar info
  pdf.text(`Nama Mahasiswa : ${seminar.mahasiswaNama || '-'}`, 20, yPos);
  yPos += 7;
  pdf.text(`NIM            : ${seminar.mahasiswaNIM || '-'}`, 20, yPos);
  yPos += 7;
  pdf.text('Judul TA       :', 20, yPos);
  const title = tugasAkhir?.judul || seminar.judulTA || '-';
  const splitTitle = pdf.splitTextToSize(title, 120);
  pdf.text(splitTitle, 70, yPos);
  yPos += splitTitle.length * 5 + 5;

  const seminarDate = format(new Date(seminar.tanggal), "EEEE, d MMMM yyyy", { locale: id });
  pdf.text(`Hari/Tanggal   : ${seminarDate}`, 20, yPos);
  yPos += 7;
  pdf.text(`Waktu          : ${seminar.waktu || '09:00'} WIB`, 20, yPos);
  yPos += 7;
  pdf.text(`Tempat         : ${seminar.tempat || '-'}`, 20, yPos);

  yPos += 15;

  // Table for attendance
  const tableData = [];

  // Identify pembimbing IDs
  const pembimbing1Id = tugasAkhir?.pembimbingId?.[0];
  const pembimbing2Id = tugasAkhir?.pembimbingId?.[1];

  // Add pembimbing first
  if (tugasAkhir?.pembimbing && Array.isArray(tugasAkhir.pembimbing)) {
    tugasAkhir.pembimbing.forEach((p, idx) => {
      tableData.push([
        tableData.length + 1,
        `Pembimbing ${idx + 1}`,
        p.nama || '-',
        p.nip || '-',
        '',
        ''
      ]);
    });
  }

  // Add penguji (non-pembimbing)
  // Use pengujiWithNIP if available (from fetch), otherwise use pengujiId/pengujiNama
  if (seminar.pengujiWithNIP && Array.isArray(seminar.pengujiWithNIP)) {
    // Use fetched data with NIP
    let pengujiCounter = 1;

    for (const pengujiData of seminar.pengujiWithNIP) {
      const pengujiId = pengujiData.id;

      // Skip if this is a pembimbing
      if (pengujiId === pembimbing1Id || pengujiId === pembimbing2Id) {
        continue;
      }

      tableData.push([
        tableData.length + 1,
        `Penguji ${pengujiCounter}`,
        pengujiData.nama || '-',
        pengujiData.nip || '-',
        '',
        ''
      ]);

      pengujiCounter++;
    }
  } else if (seminar.pengujiId && Array.isArray(seminar.pengujiId)) {
    // Fallback: use pengujiId and pengujiNama
    let pengujiCounter = 1;

    for (let i = 0; i < seminar.pengujiId.length; i++) {
      const pengujiId = seminar.pengujiId[i];

      // Skip if this is a pembimbing
      if (pengujiId === pembimbing1Id || pengujiId === pembimbing2Id) {
        continue;
      }

      // Get penguji name
      const pengujiNama = seminar.pengujiNama?.[i] || `Penguji ${pengujiCounter}`;

      tableData.push([
        tableData.length + 1,
        `Penguji ${pengujiCounter}`,
        pengujiNama,
        '-', // NIP tidak tersedia tanpa fetch dari database
        '',
        ''
      ]);

      pengujiCounter++;
    }
  }

  autoTable(pdf, {
    startY: yPos,
    head: [['No', 'Jabatan', 'Nama', 'NIP', 'Tanda Tangan', 'Keterangan']],
    body: tableData,
    styles: { fontSize: 10 },
    headStyles: { fillColor: [66, 139, 202] },
    columnStyles: {
      0: { cellWidth: 15 },
      1: { cellWidth: 40 },
      2: { cellWidth: 50 },
      3: { cellWidth: 35 },
      4: { cellWidth: 30 },
      5: { cellWidth: 30 }
    }
  });

  return pdf.output('blob');
};

/**
 * Generate PDF logbook bimbingan untuk mahasiswa
 * @param {Object} tugasAkhir - Data tugas akhir
 * @param {Object} mahasiswa - Data mahasiswa
 * @param {Array} bimbinganList - Array of bimbingan sessions
 * @param {Object} dosen - Data dosen pembimbing dengan signatureURL
 * @returns {Promise<Blob>} - PDF document as a Blob
 */
export const generateLogbookPDF = async (tugasAkhir, mahasiswa, bimbinganList, dosen) => {
  // Kompres tanda tangan dosen agar ukuran PDF kecil
  if (dosen && dosen.signatureURL) {
    dosen = { ...dosen, signatureURL: await compressImage(dosen.signatureURL, 400, 200, 0.7) };
  }
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: [215.9, 330.2] // F4 size
  });

  const programStudi = tugasAkhir?.prodi || mahasiswa?.prodi || 'REKAYASA KOSMETIK';
  const logoBase64 = await loadLogoAsBase64('logoitera.png');

  // Add letterhead
  await addLetterhead(pdf, programStudi, logoBase64);

  let yPos = 60;

  // Title
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text('LOGBOOK BIMBINGAN TUGAS AKHIR', 105, yPos, { align: 'center' });

  yPos += 15;
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'normal');

  // Student info with proper alignment
  const labelWidth = 55;
  pdf.text('Nama Mahasiswa', 20, yPos);
  pdf.text(':', labelWidth, yPos);
  pdf.text(mahasiswa?.nama || '-', labelWidth + 3, yPos);
  yPos += 6;

  pdf.text('NIM', 20, yPos);
  pdf.text(':', labelWidth, yPos);
  pdf.text(mahasiswa?.nim || '-', labelWidth + 3, yPos);
  yPos += 6;

  pdf.text('Dosen Pembimbing', 20, yPos);
  pdf.text(':', labelWidth, yPos);
  pdf.text(dosen?.nama || '-', labelWidth + 3, yPos);
  yPos += 6;

  pdf.text('Judul Tugas Akhir', 20, yPos);
  pdf.text(':', labelWidth, yPos);
  const title = tugasAkhir?.judul || '-';
  const titleWidth = 130;
  const splitTitle = pdf.splitTextToSize(title, titleWidth);
  pdf.text(splitTitle, labelWidth + 3, yPos);
  yPos += splitTitle.length * 5 + 10;

  // Filter only reviewed bimbingan and sort by date
  const reviewedBimbingan = bimbinganList
    .filter(b => b.status === 'reviewed' && b.deleted !== true)
    .sort((a, b) => {
      const dateA = new Date(a.tanggalBimbingan || a.tanggal);
      const dateB = new Date(b.tanggalBimbingan || b.tanggal);
      return dateA - dateB;
    });

  // Prepare table data
  const tableData = reviewedBimbingan.map((bimbingan, index) => {
    // Format date
    const tanggal = bimbingan.tanggalBimbingan || bimbingan.tanggal;
    let formattedDate = '-';
    try {
      if (tanggal) {
        formattedDate = format(new Date(tanggal), 'd MMMM yyyy', { locale: id });
      }
    } catch (e) {
      formattedDate = tanggal || '-';
    }

    // Get catatan/masukan dosen
    let catatan = '';
    if (bimbingan.revisiPoin && Array.isArray(bimbingan.revisiPoin) && bimbingan.revisiPoin.length > 0) {
      // Join revisiPoin array dengan bullet points
      catatan = bimbingan.revisiPoin
        .filter(p => p && p.trim() !== '')
        .map((p, i) => `${i + 1}. ${p}`)
        .join('\n');
    } else if (bimbingan.catatan) {
      catatan = bimbingan.catatan;
    }

    // Get jenis bimbingan
    let jenisBimbingan = bimbingan.jenisBimbingan || 'proposal';
    let jenisLabel = 'Proposal';
    if (jenisBimbingan === 'hasil') {
      jenisLabel = 'Hasil';
    } else if (jenisBimbingan === 'finalisasi' || jenisBimbingan === 'kompre') {
      jenisLabel = 'Finalisasi';
    }

    return [
      (index + 1).toString(),
      formattedDate,
      jenisLabel,
      catatan || '-',
      '' // Placeholder for TTD column - will be filled with signature image
    ];
  });

  // If no reviewed bimbingan
  if (tableData.length === 0) {
    tableData.push(['', 'Belum ada bimbingan yang sudah direview', '', '', '']);
  }

  // Calculate signature column width
  const sigColumnWidth = 35;

  // Add table
  autoTable(pdf, {
    startY: yPos,
    head: [['No', 'Tanggal', 'Jenis', 'Catatan/Masukan Dosen', 'TTD Dosen']],
    body: tableData,
    tableWidth: 170,
    margin: { left: 20, right: 20 },
    styles: {
      fontSize: 8,
      cellPadding: 2,
      valign: 'top',
      overflow: 'linebreak'
    },
    headStyles: {
      fillColor: [66, 135, 245],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      halign: 'center'
    },
    columnStyles: {
      0: { cellWidth: 10, halign: 'center' }, // No
      1: { cellWidth: 28, halign: 'center' }, // Tanggal
      2: { cellWidth: 20, halign: 'center' }, // Jenis
      3: { cellWidth: 80 }, // Catatan - explicit width
      4: { cellWidth: 32, halign: 'center' } // TTD
    },
    didDrawCell: (data) => {
      // Add signature image in the TTD column for body rows (column index 4 now)
      if (data.section === 'body' && data.column.index === 4 && dosen?.signatureURL) {
        try {
          const isBase64 = dosen.signatureURL.startsWith('data:image');
          const sigWidth = 25;
          const sigHeight = 12;
          const x = data.cell.x + (data.cell.width - sigWidth) / 2;
          const y = data.cell.y + 2;

          pdf.addImage(
            dosen.signatureURL,
            isBase64 ? 'JPEG' : 'PNG',
            x,
            y,
            sigWidth,
            sigHeight
          );
        } catch (sigError) {
          console.error('Error adding signature to table:', sigError);
        }
      }
    },
    didDrawPage: (data) => {
      yPos = data.cursor.y;
    }
  });

  // Get the final Y position after table
  yPos = pdf.lastAutoTable.finalY + 20;

  // Check if we need a new page for signature section
  if (yPos > 260) {
    pdf.addPage();
    yPos = 30;
  }

  // Get koordinator data
  const koordinator = await getKoordinatorTugasAkhir();
  koordinator.signatureURL = await compressImage(koordinator.signatureURL, 400, 200, 0.7);

  // Add footer with signatures - Koordinator TA on the right
  const currentDate = format(new Date(), 'd MMMM yyyy', { locale: id });

  // Location and date on the right side
  const programStudiTitleCase = programStudi.split(' ').map(word =>
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  ).join(' ');

  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Lampung Selatan, ${currentDate}`, 145, yPos, { align: 'center' });

  yPos += 7;
  pdf.text('Mengetahui,', 145, yPos, { align: 'center' });

  yPos += 5;
  pdf.setFont('helvetica', 'bold');
  pdf.text('Koordinator Tugas Akhir', 145, yPos, { align: 'center' });

  yPos += 4;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.text(`Program Studi ${programStudiTitleCase}`, 145, yPos, { align: 'center' });

  yPos += 8;

  // Add koordinator signature if available
  if (koordinator.signatureURL) {
    try {
      const isBase64 = koordinator.signatureURL.startsWith('data:image');
      const sigWidth = 40;
      const sigHeight = 20;
      pdf.addImage(
        koordinator.signatureURL,
        isBase64 ? 'JPEG' : 'PNG',
        125, // Centered around 145
        yPos,
        sigWidth,
        sigHeight
      );
      yPos += sigHeight + 3;
    } catch (sigError) {
      console.error('Error adding koordinator signature:', sigError);
      yPos += 20;
    }
  } else {
    // Placeholder if no signature
    yPos += 20;
  }

  // Koordinator name and NIP
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'normal');
  pdf.text(koordinator.nama || 'Koordinator Tugas Akhir', 145, yPos, { align: 'center' });
  yPos += 5;
  pdf.setFontSize(10);
  pdf.text(`NIP. ${koordinator.nip || '-'}`, 145, yPos, { align: 'center' });

  return pdf.output('blob');
};

/**
 * Generate PDF logbook bimbingan untuk admin dengan opsi pembimbing
 * @param {Object} tugasAkhir - Data tugas akhir
 * @param {Object} mahasiswa - Data mahasiswa
 * @param {Array} bimbinganList - Array of all bimbingan sessions
 * @param {Array} pembimbingList - Array of pembimbing objects with id, nama, nip, signatureURL
 * @param {String} mode - 'p1' for pembimbing 1 only, 'p2' for pembimbing 2 only, 'both' for combined
 * @returns {Promise<Blob>} - PDF document as a Blob
 */
export const generateLogbookPDFAdmin = async (tugasAkhir, mahasiswa, bimbinganList, pembimbingList, mode = 'both') => {
  // Kompres tanda tangan pembimbing agar ukuran PDF kecil
  pembimbingList = await Promise.all((pembimbingList || []).map(async (p) => (
    p && p.signatureURL ? { ...p, signatureURL: await compressImage(p.signatureURL, 400, 200, 0.7) } : p
  )));
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: [215.9, 330.2] // F4 size
  });

  const programStudi = tugasAkhir?.prodi || mahasiswa?.prodi || 'REKAYASA KOSMETIK';
  const logoBase64 = await loadLogoAsBase64('logoitera.png');

  // Add letterhead
  await addLetterhead(pdf, programStudi, logoBase64);

  let yPos = 60;

  // Title
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text('LOGBOOK BIMBINGAN TUGAS AKHIR', 105, yPos, { align: 'center' });

  yPos += 15;
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'normal');

  // Student info with proper alignment
  const labelWidth = 55;
  pdf.text('Nama Mahasiswa', 20, yPos);
  pdf.text(':', labelWidth, yPos);
  pdf.text(mahasiswa?.nama || '-', labelWidth + 3, yPos);
  yPos += 6;

  pdf.text('NIM', 20, yPos);
  pdf.text(':', labelWidth, yPos);
  pdf.text(mahasiswa?.nim || '-', labelWidth + 3, yPos);
  yPos += 6;

  // Show pembimbing info based on mode
  const p1 = pembimbingList[0];
  const p2 = pembimbingList[1];

  if (mode === 'both') {
    pdf.text('Pembimbing 1', 20, yPos);
    pdf.text(':', labelWidth, yPos);
    pdf.text(p1?.nama || '-', labelWidth + 3, yPos);
    yPos += 6;

    if (p2) {
      pdf.text('Pembimbing 2', 20, yPos);
      pdf.text(':', labelWidth, yPos);
      pdf.text(p2.nama || '-', labelWidth + 3, yPos);
      yPos += 6;
    }
  } else if (mode === 'p1') {
    pdf.text('Dosen Pembimbing', 20, yPos);
    pdf.text(':', labelWidth, yPos);
    pdf.text(p1?.nama || '-', labelWidth + 3, yPos);
    yPos += 6;
  } else if (mode === 'p2') {
    pdf.text('Dosen Pembimbing', 20, yPos);
    pdf.text(':', labelWidth, yPos);
    pdf.text(p2?.nama || '-', labelWidth + 3, yPos);
    yPos += 6;
  }

  pdf.text('Judul Tugas Akhir', 20, yPos);
  pdf.text(':', labelWidth, yPos);
  const title = tugasAkhir?.judul || '-';
  const titleWidth = 130;
  const splitTitle = pdf.splitTextToSize(title, titleWidth);
  pdf.text(splitTitle, labelWidth + 3, yPos);
  yPos += splitTitle.length * 5 + 10;

  // Filter bimbingan based on mode
  let filteredBimbingan = bimbinganList.filter(b => b.status === 'reviewed' && b.deleted !== true);

  if (mode === 'p1' && p1) {
    filteredBimbingan = filteredBimbingan.filter(b => b.pembimbingId === p1.id);
  } else if (mode === 'p2' && p2) {
    filteredBimbingan = filteredBimbingan.filter(b => b.pembimbingId === p2.id);
  }
  // For 'both' mode, include all reviewed bimbingan

  // Sort by date
  filteredBimbingan.sort((a, b) => {
    const dateA = new Date(a.tanggalBimbingan || a.tanggal);
    const dateB = new Date(b.tanggalBimbingan || b.tanggal);
    return dateA - dateB;
  });

  // Prepare table data
  const isBothMode = mode === 'both';
  const tableData = filteredBimbingan.map((bimbingan, index) => {
    // Format date
    const tanggal = bimbingan.tanggalBimbingan || bimbingan.tanggal;
    let formattedDate = '-';
    try {
      if (tanggal) {
        formattedDate = format(new Date(tanggal), 'd MMMM yyyy', { locale: id });
      }
    } catch (e) {
      formattedDate = tanggal || '-';
    }

    // Get jenis bimbingan
    let jenisBimbingan = bimbingan.jenisBimbingan || 'proposal';
    let jenisLabel = 'Proposal';
    if (jenisBimbingan === 'hasil') {
      jenisLabel = 'Hasil';
    } else if (jenisBimbingan === 'finalisasi' || jenisBimbingan === 'kompre') {
      jenisLabel = 'Finalisasi';
    }

    // Get catatan/masukan dosen
    let catatan = '';
    if (bimbingan.revisiPoin && Array.isArray(bimbingan.revisiPoin) && bimbingan.revisiPoin.length > 0) {
      catatan = bimbingan.revisiPoin
        .filter(p => p && p.trim() !== '')
        .map((p, i) => `${i + 1}. ${p}`)
        .join('\n');
    } else if (bimbingan.catatan) {
      catatan = bimbingan.catatan;
    }

    // Determine which pembimbing this bimbingan belongs to
    const pembimbingIdx = pembimbingList.findIndex(p => p.id === bimbingan.pembimbingId);

    if (isBothMode) {
      // For both mode, add pembimbing label and TTD column
      const pembLabel = pembimbingIdx === 0 ? 'P1' : pembimbingIdx === 1 ? 'P2' : '-';
      return [
        (index + 1).toString(),
        formattedDate,
        jenisLabel,
        pembLabel,
        catatan || '-',
        '' // TTD placeholder
      ];
    } else {
      return [
        (index + 1).toString(),
        formattedDate,
        jenisLabel,
        catatan || '-',
        '' // TTD placeholder
      ];
    }
  });

  // If no reviewed bimbingan
  if (tableData.length === 0) {
    if (isBothMode) {
      tableData.push(['', 'Belum ada bimbingan yang sudah direview', '', '', '', '']);
    } else {
      tableData.push(['', 'Belum ada bimbingan yang sudah direview', '', '', '']);
    }
  }

  // Calculate signature column width
  const sigColumnWidth = 30;

  // Get the active dosen for signature based on mode
  const getDosenForRow = (rowIndex) => {
    if (mode === 'p1') return p1;
    if (mode === 'p2') return p2;
    // For 'both' mode, get the dosen based on the bimbingan's pembimbingId
    if (rowIndex < filteredBimbingan.length) {
      const bimbingan = filteredBimbingan[rowIndex];
      return pembimbingList.find(p => p.id === bimbingan.pembimbingId);
    }
    return null;
  };

  // Define table structure based on mode
  const tableHead = isBothMode
    ? [['No', 'Tanggal', 'Jenis', 'Pemb.', 'Catatan/Masukan Dosen', 'TTD']]
    : [['No', 'Tanggal', 'Jenis', 'Catatan/Masukan Dosen', 'TTD']];

  const columnStyles = isBothMode
    ? {
      0: { cellWidth: 10, halign: 'center' }, // No
      1: { cellWidth: 25, halign: 'center' }, // Tanggal
      2: { cellWidth: 18, halign: 'center' }, // Jenis
      3: { cellWidth: 12, halign: 'center' }, // Pemb
      4: { cellWidth: 75 }, // Catatan - explicit width
      5: { cellWidth: 30, halign: 'center' } // TTD
    }
    : {
      0: { cellWidth: 10, halign: 'center' },
      1: { cellWidth: 28, halign: 'center' },
      2: { cellWidth: 20, halign: 'center' },
      3: { cellWidth: 80 }, // Catatan - explicit width
      4: { cellWidth: 32, halign: 'center' }
    };

  const ttdColumnIndex = isBothMode ? 5 : 4;

  // Add table
  autoTable(pdf, {
    startY: yPos,
    head: tableHead,
    body: tableData,
    tableWidth: 170,
    margin: { left: 20, right: 20 },
    styles: {
      fontSize: 8,
      cellPadding: 2,
      valign: 'top',
      overflow: 'linebreak'
    },
    headStyles: {
      fillColor: [66, 135, 245],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      halign: 'center'
    },
    columnStyles: columnStyles,
    didDrawCell: (data) => {
      // Add signature image in the TTD column for body rows
      if (data.section === 'body' && data.column.index === ttdColumnIndex) {
        const dosen = getDosenForRow(data.row.index);
        if (dosen?.signatureURL) {
          try {
            const isBase64 = dosen.signatureURL.startsWith('data:image');
            const sigWidth = 22;
            const sigHeight = 10;
            const x = data.cell.x + (data.cell.width - sigWidth) / 2;
            const y = data.cell.y + 2;

            pdf.addImage(
              dosen.signatureURL,
              isBase64 ? 'JPEG' : 'PNG',
              x,
              y,
              sigWidth,
              sigHeight
            );
          } catch (sigError) {
            console.error('Error adding signature to table:', sigError);
          }
        }
      }
    },
    didDrawPage: (data) => {
      yPos = data.cursor.y;
    }
  });

  // Get the final Y position after table
  yPos = pdf.lastAutoTable.finalY + 20;

  // Check if we need a new page for signature section
  if (yPos > 260) {
    pdf.addPage();
    yPos = 30;
  }

  // Get koordinator data
  const koordinator = await getKoordinatorTugasAkhir();
  koordinator.signatureURL = await compressImage(koordinator.signatureURL, 400, 200, 0.7);

  // Add footer with signatures - Koordinator TA on the right
  const currentDate = format(new Date(), 'd MMMM yyyy', { locale: id });

  // Location and date on the right side
  const programStudiTitleCase = programStudi.split(' ').map(word =>
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  ).join(' ');

  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Lampung Selatan, ${currentDate}`, 145, yPos, { align: 'center' });

  yPos += 7;
  pdf.text('Mengetahui,', 145, yPos, { align: 'center' });

  yPos += 5;
  pdf.setFont('helvetica', 'bold');
  pdf.text('Koordinator Tugas Akhir', 145, yPos, { align: 'center' });

  yPos += 4;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.text(`Program Studi ${programStudiTitleCase}`, 145, yPos, { align: 'center' });

  yPos += 8;

  // Add koordinator signature if available
  if (koordinator.signatureURL) {
    try {
      const isBase64 = koordinator.signatureURL.startsWith('data:image');
      const sigWidth = 40;
      const sigHeight = 20;
      pdf.addImage(
        koordinator.signatureURL,
        isBase64 ? 'JPEG' : 'PNG',
        125,
        yPos,
        sigWidth,
        sigHeight
      );
      yPos += sigHeight + 3;
    } catch (sigError) {
      console.error('Error adding koordinator signature:', sigError);
      yPos += 20;
    }
  } else {
    yPos += 20;
  }

  // Koordinator name and NIP
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'normal');
  pdf.text(koordinator.nama || 'Koordinator Tugas Akhir', 145, yPos, { align: 'center' });
  yPos += 5;
  pdf.setFontSize(10);
  pdf.text(`NIP. ${koordinator.nip || '-'}`, 145, yPos, { align: 'center' });

  return pdf.output('blob');
};

export default generateSeminarDocument;